import { useMemo } from 'react';

import * as Sentry from '@sentry/react';
import type { QueryFunctionContext, UseQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery as useTanInfiniteQuery, useQuery as useTanQuery } from '@tanstack/react-query';
import { AGGREGATE_NAMES, USER_RESOLVERS } from 'cpool-constants';
import type { SchemaUserResolver_GetAclPermissionsArgs } from 'cpool-tables/backend-resolvers';
import { schemaUserResolver_GetAclPermissions } from 'cpool-tables/backend-resolvers';
import { z } from 'zod';
import type { ZodSchema } from 'zod';

import { APICommunicator } from '~/api/APICommunicator';

/**
 * Strips null and undefined so the query key matches what {@link buildQueryString} actually sends.
 * Both null and undefined are omitted from the URL, so they must not distinguish cache keys either.
 *
 * Convention (must stay in sync with the enabled guard below):
 *   undefined → required param not ready; the enabled guard will block the query entirely.
 *   null      → optional param absent; query fires but param is omitted from URL and key.
 *   value     → param is serialized and included.
 *
 * TODO: Align callers that pass null when they mean "optional/absent" to instead omit the key,
 * then extend the enabled guard to also block on null and remove the null check here.
 * Callers to update:
 *   - useAcceptedOffers.tsx: `filter: offerFilter || null`
 *   - TeamMemberView.tsx:    `{ organization: user?.organization ?? null, teamLeaderId: selectedUser?.id ?? null }`
 *   - dashboard/index.tsx:   `{ position: null }` (already excluded from the guard via the position exemption)
 */
export function searchParamsForQueryKey(searchParams: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(searchParams).filter(([, value]) => value !== undefined && value !== null),
    );
}

/** Serializes params to a query string; skips null/undefined (unlike raw URLSearchParams). See {@link searchParamsForQueryKey} for the null/undefined convention. */
export function buildQueryString(params: Record<string, unknown>, extra: Record<string, string> = {}): string {
    const modParams: Record<string, string> = { ...extra };
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (value instanceof Date) {
            modParams[key] = value.toISOString();
        } else if (typeof value === 'boolean' || typeof value === 'number') {
            modParams[key] = String(value);
        } else if (typeof value === 'object') {
            modParams[key] = JSON.stringify(value);
        } else {
            modParams[key] = String(value);
        }
    });
    const qs = new URLSearchParams(modParams).toString();
    return qs.length > 0 ? `?${qs}` : '';
}

type RequestMeta<TSchema extends ZodSchema> = {
    queryUrl: string;
    schema: TSchema;
    validationCallback?: (data: z.infer<TSchema>) => void;
};

export async function fetcher<TSchema extends ZodSchema>({
    meta,
    signal,
}: Partial<QueryFunctionContext>): Promise<z.infer<TSchema>> {
    const { queryUrl, schema, validationCallback } = meta as RequestMeta<TSchema>;

    const res = await APICommunicator.apiFetch({ context: { signal }, route: queryUrl });
    const parsedBody = await res.json();

    try {
        const parsed = await schema.parseAsync(parsedBody);
        if (validationCallback) validationCallback(parsed);

        return parsed;
    } catch (e) {
        // Im test environment Validation Fehler hier loggen weil die sonst verschluckt werden
        if (import.meta.env.MODE === 'test' || import.meta.env.MODE === 'development') {
            console.error(
                'Body validation failed!',
                e instanceof z.ZodError
                    ? {
                          queryUrl,
                          zodIssues: e.issues.map((issue) => ({
                              ...issue,
                              path: issue.path.join('.'),
                          })),
                      }
                    : { error: e, queryUrl },
            );
        }
        Sentry.captureException(e);
        throw e;
    }
}

export type Tanstack<TSchema extends ZodSchema> = Omit<UseQueryOptions<z.infer<TSchema>>, 'queryKey'>;

interface TanstackObject<TSchema extends ZodSchema> {
    tanstack?: Tanstack<TSchema>;
}

/** Scope of the getAclPermissions query that gates a resolver; `{}` = signed-in user without further context. */
export type AclScope = SchemaUserResolver_GetAclPermissionsArgs;

export const aclSearchParams = ({ actionFilter, chatLinkInfo, loanRequestId, userId }: AclScope) => {
    const searchParams: Record<string, unknown> = {};
    if (loanRequestId) searchParams.loanRequestId = loanRequestId;
    if (userId) searchParams.userId = userId;
    if (chatLinkInfo) searchParams.chatLinkInfo = chatLinkInfo;
    if (actionFilter) searchParams.actionFilter = actionFilter;
    return searchParams;
};

const ACL_DISABLED_KEY = ['acl-gate-disabled'] as const;

// Note: short on purpose — dedupes loader prefetch + mount refetches, but lead-scoped perms change on reassignment
export const ACL_PERMISSIONS_STALE_TIME = 5_000;

/** Resolves whether a resolver may be queried; pending until the real ACL arrives (never optimistic). */
export function useResolverAclGate(acl: AclScope | undefined, aggregateName: string, resolverName: string) {
    const searchParams = acl ? aclSearchParams(acl) : {};
    const queryUrl = `/query/${AGGREGATE_NAMES.USER}/${USER_RESOLVERS.getAclPermissions}`;
    const meta: RequestMeta<typeof schemaUserResolver_GetAclPermissions> = {
        queryUrl: queryUrl + buildQueryString(searchParams),
        schema: schemaUserResolver_GetAclPermissions,
    };
    const { data, isPending } = useTanQuery({
        enabled: !!acl,
        meta,
        queryFn: () => fetcher<typeof schemaUserResolver_GetAclPermissions>({ meta }),
        // Note: without acl a sentinel key keeps the query from subscribing to (and re-rendering on) ACL changes
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: acl
            ? [AGGREGATE_NAMES.USER, USER_RESOLVERS.getAclPermissions, searchParamsForQueryKey(searchParams)]
            : ACL_DISABLED_KEY,
        staleTime: ACL_PERMISSIONS_STALE_TIME,
    });
    if (!acl) return { allowed: undefined, pending: false };
    const resolvers = data?.resolvers as Record<string, Record<string, boolean> | undefined> | undefined;
    return { allowed: resolvers?.[aggregateName]?.[resolverName] === true, pending: isPending };
}

interface UseQueryData<TSchema extends ZodSchema> extends TanstackObject<TSchema> {
    queryKey: readonly any[];
    schema: TSchema;
    queryUrl: string;
    searchParams?: Record<string, unknown>;
    validationCallback?: (data: z.infer<TSchema>) => void;
    queryCallback?: (data: z.infer<TSchema>) => void;
    /** undefined = no acl gate */
    aclAllowed?: boolean;
}

export function useQuery<TSchema extends ZodSchema>({
    aclAllowed,
    queryCallback,
    queryKey,
    queryUrl,
    schema,
    searchParams = {},
    tanstack,
    validationCallback,
}: UseQueryData<TSchema>) {
    const searchParamsString = useMemo(() => buildQueryString(searchParams), [searchParams]);

    const meta: RequestMeta<TSchema> = { queryUrl: queryUrl + searchParamsString, schema, validationCallback };
    // Blocks the query when a required param is undefined. Intentionally does not check null —
    // see searchParamsForQueryKey for the null/undefined convention and the TODO to unify them.
    // Note: an acl gate counts as an explicit enabled decision (skips the undefined-param guard), like the
    // `enabled: permissions.resolvers…` it replaces
    const enabled =
        (aclAllowed ?? true) &&
        (tanstack?.enabled ??
            (aclAllowed !== undefined ||
                !Object.entries(searchParams).some(([key, value]) => key !== 'position' && value === undefined)));

    return useTanQuery({
        ...tanstack,
        enabled,
        meta,
        queryFn: async () => {
            const data = await fetcher<TSchema>({ meta });
            if (queryCallback) queryCallback(data);
            return data;
        },
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
    });
}

interface UseAggregateQueryData<TSchema extends ZodSchema> extends TanstackObject<TSchema> {
    /** Gates the query on getAclPermissions for this aggregate/resolver, scoped as given. */
    acl?: AclScope;
    schema: TSchema;
    aggregateName: string;
    resolverName: string;
    searchParams?: Record<string, unknown>;
    validationCallback?: (data: z.infer<TSchema>) => void;
    queryCallback?: (data: z.infer<TSchema>) => void;
}

export function useAggregateQuery<TSchema extends ZodSchema>({
    acl,
    aggregateName,
    queryCallback,
    resolverName,
    schema,
    searchParams = {},
    tanstack,
    validationCallback,
}: UseAggregateQueryData<TSchema>) {
    const queryUrl = `/query/${aggregateName}/${resolverName}`;
    const queryKey = [aggregateName, resolverName, searchParamsForQueryKey(searchParams)];
    const aclGate = useResolverAclGate(acl, aggregateName, resolverName);

    const result = useQuery<TSchema>({
        aclAllowed: aclGate.allowed,
        queryCallback,
        queryKey,
        queryUrl,
        schema,
        searchParams,
        tanstack,
        validationCallback,
    });
    // Note: spread only while pending — spreading defeats react-query's tracked-props re-render optimisation
    return aclGate.pending ? { ...result, isLoading: true } : result;
}

interface UseAggregateInfiniteQueryData<TSchema extends ZodSchema> {
    acl?: AclScope;
    aggregateName: string;
    resolverName: string;
    pageSize: number;
    schema: TSchema;
    searchParams?: Record<string, unknown>;
    enabled?: boolean;
}

export function useAggregateInfiniteQuery<TSchema extends z.ZodArray<z.ZodTypeAny>>({
    acl,
    aggregateName,
    enabled = true,
    pageSize,
    resolverName,
    schema,
    searchParams = {},
}: UseAggregateInfiniteQueryData<TSchema>) {
    const queryUrl = `/query/${aggregateName}/${resolverName}`;
    const queryKey = [aggregateName, resolverName, searchParamsForQueryKey(searchParams)];

    const buildUrl = (offset: number) =>
        queryUrl + buildQueryString(searchParams, { limit: String(pageSize), offset: String(offset) });
    const aclGate = useResolverAclGate(acl, aggregateName, resolverName);

    const result = useTanInfiniteQuery({
        enabled: (aclGate.allowed ?? true) && enabled,
        getNextPageParam: (lastPage: z.infer<TSchema>, _allPages: Array<z.infer<TSchema>>, lastPageParam: number) =>
            lastPage.length === pageSize ? lastPageParam + pageSize : undefined,
        initialPageParam: 0,
        queryFn: async ({ pageParam, signal }: QueryFunctionContext<typeof queryKey, number>) => {
            const url = buildUrl(pageParam);
            const meta: RequestMeta<TSchema> = { queryUrl: url, schema };
            return fetcher<TSchema>({ meta, signal });
        },
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey,
    });
    return aclGate.pending ? { ...result, isLoading: true } : result;
}
